import {
  DynamicOption,
  stagger,
  TargetAndTransition,
  MotionNodeAnimationOptions,
} from "motion/react";
import { assert } from "@greeter/assert";

export const blurInOut: MotionNodeAnimationOptions = {
  initial: { filter: "blur(2px)" },
  animate: { filter: "blur(0)" },
  exit: { filter: "blur(2px)" },
};

export const fadeInOut: MotionNodeAnimationOptions = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const fromTop: MotionNodeAnimationOptions = {
  initial: { y: -10 },
  animate: { y: 0 },
  exit: { y: -10 },
};

export const fromBottom: MotionNodeAnimationOptions = {
  initial: { y: 10 },
  animate: { y: 0 },
  exit: { y: 10 },
};

export const smooshVertically: MotionNodeAnimationOptions = {
  exit: {
    height: 0,
    paddingBottom: 0,
    paddingTop: 0,
    marginTop: 0,
    marginBottom: 0,
  },
};

export const modal: MotionNodeAnimationOptions = {
  initial: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    width: "100%",
    height: "100%",
    maxHeight: "100vh",
    zIndex: 1000000,
  },
  animate: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    width: "100%",
    height: "100%",
    maxHeight: "100vh",
    zIndex: 1000000,
  },
  exit: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    width: "100%",
    height: "100%",
    maxHeight: "100vh",
    zIndex: 1000000,
  },
};

function recursiveMerge<T>(target: any, source: any): T {
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (
        typeof target[key] === "object" &&
        target[key] !== null &&
        !Array.isArray(target[key]) &&
        typeof source[key] === "object" &&
        source[key] !== null &&
        !Array.isArray(source[key])
      ) {
        // If both are objects, merge recursively
        target[key] = recursiveMerge(target[key], source[key]);
      } else {
        // Otherwise, just assign the source property to the target
        target[key] = source[key];
      }
    }
  }
  return target;
}

export function combine(
  ...props: MotionNodeAnimationOptions[]
): MotionNodeAnimationOptions {
  let target: MotionNodeAnimationOptions = {
    initial: {},
    animate: {},
    exit: {},
  };
  for (const prop of props) {
    recursiveMerge(target, prop);
  }
  return target;
}

export const dropInOutFromTop = combine(blurInOut, fadeInOut, fromTop);
export const dropInOutFromBottom = combine(blurInOut, fadeInOut, fromBottom);
export const dropInOutSmooshFromTop = combine(
  blurInOut,
  fadeInOut,
  fromTop,
  smooshVertically
);
export const dropInOutSmooshFromBottom = combine(
  blurInOut,
  fadeInOut,
  fromTop,
  smooshVertically
);
export function unwrapObject(
  obj: MotionNodeAnimationOptions["animate"]
): TargetAndTransition {
  assert(
    typeof obj === "object",
    "animation target and transition is NOT an object"
  );
  assert(
    !Array.isArray(obj),
    "animation target and transition is of invalid type Array<string>"
  );
  assert(
    !("start" in obj),
    "animation target and transition is of invalid type AnimationControls"
  );
  return obj;
}

// No need to recreate staggers if they already exist
type Delay = number;
const staggerFirstCache = new Map<Delay, DynamicOption<number>>();
const staggerLastCache = new Map<Delay, DynamicOption<number>>();
export function staggerForwardsBackwards(
  props: MotionNodeAnimationOptions,
  delay: Delay,
  index: number,
  length: number
): MotionNodeAnimationOptions {
  let staggerForwards = staggerFirstCache.get(delay);
  if (!staggerForwards) {
    staggerForwards = stagger(delay, { from: "first" });
    staggerFirstCache.set(delay, staggerForwards);
  }
  let staggerBackwards = staggerLastCache.get(delay);
  if (!staggerBackwards) {
    staggerBackwards = stagger(delay, { from: "last" });
    staggerLastCache.set(delay, staggerBackwards);
  }

  return {
    initial: props.initial,
    animate: {
      ...unwrapObject(props.animate),
      transition: {
        delay: staggerForwards(index, length),
      },
    },
    exit: {
      ...unwrapObject(props.exit),
      transition: {
        delay: staggerBackwards(index, length),
      },
    },
  };
}
